"use client";

import React from "react";
import type { CustomerLanguage } from "@/lib/customer-language";
import type { NaamMatchingResponse } from "@/lib/api/matching-naam";
import { KootaBreakdown } from "../KootaBreakdown";
import { matchingCopy } from "../matching-copy";
import { nakshatraLabel } from "./naam-cell";

/**
 * The naam-mode result — boundary FIRST, then the number.
 *
 * Order is load-bearing, not stylistic. The arithmetic here is complete (36/36,
 * a verdict, all eight kootas), so the only thing that can be wrong is the
 * PREMISE: that these aksharas came from the naamkaran. A complete-looking score
 * hides that better than a partial one would, which is why rule 29 #7 requires
 * the boundary be met before the number and why it may not sit in an accordion,
 * a footnote, or below the verdict.
 *
 * Deliberately absent: MangalDoshaStatus (it renders the literal "Non-Manglik"
 * from a boolean this mode cannot compute), the lagna rows of MatchingScoreCard
 * (a naam-akshar supplies no lagna), and the detailed-report path (needs both
 * full charts). None of those are omissions to be filled in later.
 */
export function NaamResultPanel({
  result,
  language,
  onSwitchToBirthMode,
  onReset,
}: {
  result: NaamMatchingResponse;
  language: CustomerLanguage;
  onSwitchToBirthMode: () => void;
  onReset: () => void;
}) {
  const copy = matchingCopy(language);
  return (
    <div className="space-y-6">
      <div className="card-sacred p-6 sm:p-8">
        <NaamBoundary result={result} language={language} />
        <NaamScore result={result} language={language} />
      </div>

      <KootaBreakdown result={result} language={language} />

      <div className="card-sacred p-6">
        <h2 className="mb-2 font-serif text-title font-bold text-ish-dark">
          {copy.recommendation}
        </h2>
        <p className="text-body leading-relaxed text-ish-text-muted">{result.recommendation}</p>
      </div>

      <div className="flex flex-wrap gap-3">
        <button
          type="button"
          onClick={onSwitchToBirthMode}
          className="btn-primary px-5 py-3 text-body"
        >
          {copy.naamSwitchToBirth}
        </button>
        <button
          type="button"
          onClick={onReset}
          className="text-body-small font-semibold text-intent-brand"
        >
          {copy.matchAnother}
        </button>
      </div>
    </div>
  );
}

function NaamBoundary({
  result,
  language,
}: {
  result: NaamMatchingResponse;
  language: CustomerLanguage;
}) {
  const copy = matchingCopy(language);
  return (
    <section
      data-testid="naam-boundary"
      aria-labelledby="naam-boundary-heading"
      className="mb-6 rounded-lg border border-ish-border bg-surface-warm p-4"
    >
      <h2 id="naam-boundary-heading" className="mb-2 text-body font-bold text-ish-dark font-serif">
        {copy.naamBoundaryHeading}
      </h2>
      <p className="mb-2 text-body leading-relaxed text-ish-dark">{copy.naamBoundaryPremise}</p>
      <p className="mb-2 text-body-small leading-relaxed text-ish-text-muted">
        {copy.naamBoundaryJanma}
      </p>
      <p className="mb-2 text-body-small leading-relaxed text-ish-text-muted">
        {copy.naamBoundaryMangal}
      </p>
      {/* The server's own boundary string, rendered rather than re-typed, so the
          data-carried disclosure and the page copy cannot drift apart. */}
      <p className="text-body-small leading-relaxed text-ish-text-muted">{result.boundary}</p>
    </section>
  );
}

/**
 * The verdict's tone, derived from the SAME thresholds the engine bands on
 * (>=25 highly compatible, >=18 meets the traditional threshold, below is not).
 *
 * The response also carries a `verdict_color` hex, but rendering a server hex as
 * an inline style forks the semantic colour roles. Deriving from the score keeps
 * the tone and the verdict text agreeing by construction.
 */
function verdictToneClass(result: NaamMatchingResponse): string {
  if (result.total_score >= 25) return "text-success";
  if (result.total_score >= 18) return "text-warning";
  return "text-error";
}

function NaamScore({
  result,
  language,
}: {
  result: NaamMatchingResponse;
  language: CustomerLanguage;
}) {
  const copy = matchingCopy(language);
  return (
    <div data-testid="naam-score">
      <p className="font-serif text-display font-bold leading-none text-ish-dark">
        {result.total_score}
        <span className="text-title text-ish-text-faint">/{result.max_score}</span>
      </p>
      <p className={`mt-1 text-body font-semibold ${verdictToneClass(result)}`}>{result.verdict}</p>
      <p className="mt-3 text-body-small text-ish-text-muted">
        {copy.moon}: {nakshatraLabel(result.person_a.moon_nakshatra, language)} ({copy.naamPada}{" "}
        {result.person_a.pada}) &middot; {nakshatraLabel(result.person_b.moon_nakshatra, language)}{" "}
        ({copy.naamPada} {result.person_b.pada})
      </p>
    </div>
  );
}
